import { memo, type ReactNode } from 'react'
import { cn } from '../../../../lib/utils'

/** Agent type for color styling */
export type AgentType = 'character' | 'location' | 'director' | 'object' | 'storyboard' | 'scriptwriter' | 'video' | 'sound'

interface AssetListItemProps {
  icon: ReactNode
  label: string
  name: string
  agentType?: AgentType
  thumbnailUrl?: string | null
  status?: 'pending' | 'generating' | 'complete' | 'error'
  onClick?: () => void
  className?: string
}

const agentColorMap: Record<AgentType, { border: string; text: string }> = {
  character: { border: 'border-agent-character/30', text: 'text-agent-character' },
  location: { border: 'border-agent-location/30', text: 'text-agent-location' },
  director: { border: 'border-agent-director/30', text: 'text-agent-director' },
  object: { border: 'border-agent-object/30', text: 'text-agent-object' },
  storyboard: { border: 'border-agent-storyboard/30', text: 'text-agent-storyboard' },
  scriptwriter: { border: 'border-agent-scriptwriter/30', text: 'text-agent-scriptwriter' },
  video: { border: 'border-agent-video/30', text: 'text-agent-video' },
  sound: { border: 'border-agent-sound/30', text: 'text-agent-sound' },
}

export const AssetListItem = memo(function AssetListItem({
  icon,
  label,
  name,
  agentType = 'character',
  thumbnailUrl,
  status,
  onClick,
  className,
}: AssetListItemProps) {
  const isGenerating = status === 'generating'
  const isError = status === 'error'
  const isComplete = status === 'complete'

  const colors = agentColorMap[agentType]

  return (
    <div
      className={cn(
        'flex items-center gap-3 py-2',
        onClick && 'cursor-pointer hover:bg-surface-2/50 -mx-2 px-2 rounded-lg transition-colors',
        className
      )}
      onClick={onClick}
    >
      {/* Icon container - rounded square */}
      <div
        className={cn(
          'relative w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden',
          'bg-surface-0 border',
          isGenerating ? 'border-primary/50' : isError ? 'border-destructive/50' : isComplete ? colors.border : 'border-border'
        )}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
              const sibling = e.currentTarget.nextElementSibling
              if (sibling instanceof HTMLElement) sibling.style.display = 'flex'
            }}
          />
        ) : null}
        {/* Icon fallback (shown when no thumbnail or thumbnail fails) */}
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            colors.text,
            thumbnailUrl && 'hidden'
          )}
        >
          {icon}
        </div>

        {/* Loading spinner overlay */}
        {isGenerating && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Status indicator dot */}
        {isComplete && (
          <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-success rounded-full" />
        )}
        {isError && (
          <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-destructive rounded-full" />
        )}
      </div>

      {/* Text content */}
      <div className="flex flex-col min-w-0">
        <span className="text-xs font-medium text-muted-foreground capitalize">
          {label}
        </span>
        <span className="text-sm font-medium text-foreground truncate">
          {name}
        </span>
      </div>
    </div>
  )
})

AssetListItem.displayName = 'AssetListItem'
